import { authClient } from '@/auth/auth';
import { financialDataRateLimitMessage, parseRetryAfter } from '@/shared/utils/financialRateLimitHttp';
import type { ConnectionType, ConnectionTypeDetails } from 'quadratic-shared/typesAndSchemasConnections';
import z from 'zod';
const API_URL = import.meta.env.VITE_QUADRATIC_CONNECTION_URL;

// Skip auth redirect: connection requests can fire from background fetchers/hooks
// while a public file is being viewed anonymously. Letting the request go without
// an Authorization header lets the connection service return a clean 401 the
// caller can surface, instead of forcibly bouncing the viewer to the login page.
const jwtHeader = async (): Promise<HeadersInit> => {
  const jwt = await authClient.getTokenOrRedirect(true);
  const headers: HeadersInit = { 'content-type': 'application/json' };
  if (jwt) {
    headers.authorization = `Bearer ${jwt}`;
  }
  return headers;
};

// TODO: these should come from the connection service definition for these
// endpoints but for now, they are defined here
const TestSchema = z.object({ connected: z.boolean(), message: z.string().nullable() });

/**
 * Turn a non-2xx connection-service response into a message we can show the
 * user. These bodies are NOT the endpoint's normal JSON shape — quadratic-connection
 * renders every `ConnectionError` as a bare string (see its `IntoResponse` impl),
 * and passes it through `clean_errors`, which `Debug`-formats the string. So the
 * body arrives JSON-quoted and escaped (`"Ssh error: ..."`), which `res.json()`
 * happily returns as a JS *string*.
 *
 * Falls back to a status-only message when the body isn't one of those: a
 * proxy/gateway can answer with an HTML error page or an empty body, and dumping
 * markup into a form field helps nobody.
 */
const readConnectionErrorMessage = async (res: Response): Promise<string> => {
  const fallback = `The connection service returned an error (${res.status}). Please try again.`;

  let text = '';
  try {
    text = (await res.text()).trim();
  } catch {
    return fallback;
  }
  if (!text) return fallback;

  try {
    const parsed = JSON.parse(text);
    if (typeof parsed === 'string' && parsed.trim()) return parsed.trim();
    // Defensive: if a route ever returns the `{ message }` shape on an error.
    const message = (parsed as { message?: unknown } | null)?.message;
    if (typeof message === 'string' && message.trim()) return message.trim();
  } catch {
    // Not JSON at all (HTML error page, plain text from an intermediary).
  }

  return fallback;
};

const SqlSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  database: z.string(),
  tables: z.array(
    z.object({
      name: z.string(),
      schema: z.string(), // public or ...?
      columns: z.array(
        z.object({
          name: z.string(),
          type: z.string(),
          is_nullable: z.boolean(),
        })
      ),
    })
  ),
});
export type SqlSchemaResponse = z.infer<typeof SqlSchema>;

const StaticIpsSchema = z.object({
  static_ips: z.array(z.string()),
});
type StaticIpsResponse = z.infer<typeof StaticIpsSchema>;

// To add a new financial endpoint, add a single entry here.
// FinancialEndpoint, FinancialRequestMap, and runtime validation all derive from this.
const financialSchemas = {
  'stock-prices': z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    frequency: z.string().nullable().optional(),
  }),
  'financial-statements': z.object({
    identifier: z.string(),
    statement: z.string(),
    fiscal_year: z.number(),
    fiscal_period: z.string(),
  }),
  'realtime-price': z.object({
    identifier: z.string(),
  }),
  dividends: z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
  }),
  splits: z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
  }),
  company: z.object({
    identifier: z.string(),
  }),
  'company-news': z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    page_size: z.number().nullable().optional(),
  }),
  'data-point': z.object({
    identifier: z.string(),
    tag: z.string(),
  }),
  'data-point-number': z.object({
    identifier: z.string(),
    tag: z.string(),
  }),
  'data-point-text': z.object({
    identifier: z.string(),
    tag: z.string(),
  }),
  'historical-data': z.object({
    identifier: z.string(),
    tag: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    frequency: z.string().nullable().optional(),
    sort_order: z.string().nullable().optional(),
  }),
  'intraday-prices': z.object({
    identifier: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    interval_size: z.string().nullable().optional(),
  }),
  technicals: z.object({
    identifier: z.string(),
    indicator: z.string(),
    period: z.number().nullable().optional(),
    signal_period: z.number().nullable().optional(),
    fast_period: z.number().nullable().optional(),
    slow_period: z.number().nullable().optional(),
    standard_deviations: z.number().nullable().optional(),
    constant: z.number().nullable().optional(),
    price_key: z.string().nullable().optional(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
  }),
  'search-companies': z.object({
    query: z.string(),
    page_size: z.number().nullable().optional(),
  }),
  'search-securities': z.object({
    query: z.string(),
    page_size: z.number().nullable().optional(),
  }),
  'economic-data': z.object({
    identifier: z.string(),
    tag: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    sort_order: z.string().nullable().optional(),
  }),
  'sic-data': z.object({
    identifier: z.string(),
    tag: z.string(),
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    sort_order: z.string().nullable().optional(),
  }),
  ipos: z.object({
    start_date: z.string().nullable().optional(),
    end_date: z.string().nullable().optional(),
    status: z.string().nullable().optional(),
  }),
} satisfies Record<string, z.ZodSchema>;

export type FinancialEndpoint = keyof typeof financialSchemas;
type FinancialRequestMap = { [E in FinancialEndpoint]: z.infer<(typeof financialSchemas)[E]> };
export type FinancialRequestParams = FinancialRequestMap[FinancialEndpoint];

export type QueryPreviewColumn = { name: string; data_type: string };
export type QueryPreviewResponse = {
  columns: QueryPreviewColumn[];
  rows: Array<Record<string, unknown>>;
};

export type FetchPlaidInstitutionLogoResult = {
  blob: Blob | null;
  /** True when the connection service responded with HTTP 404 (no PNG body). */
  notFound: boolean;
};

/**
 * Fetches one Plaid institution logo from the connection service. When `opts.forceCacheRefresh` is
 * true, requests `?force_cache_refresh=true` (same query param as schema routes) so the server drops
 * its in-process cache for that institution id before calling Plaid.
 */
export async function fetchPlaidInstitutionLogo(
  institutionId: string,
  teamUuid: string,
  opts?: { timeout?: number; forceCacheRefresh?: boolean }
): Promise<FetchPlaidInstitutionLogoResult> {
  const timeout = opts?.timeout ?? 30000;
  const forceCacheRefresh = opts?.forceCacheRefresh ?? false;
  try {
    const loggedIn = await authClient.isAuthenticated();
    if (!loggedIn) return { blob: null, notFound: false };

    const headers = new Headers(await jwtHeader());
    headers.set('X-Team-Id', teamUuid);

    const qs = forceCacheRefresh ? '?force_cache_refresh=true' : '';
    const res = await fetch(`${API_URL}/plaid/logo/${encodeURIComponent(institutionId)}${qs}`, {
      method: 'GET',
      headers,
      signal: AbortSignal.timeout(timeout),
    });

    if (res.status === 404) return { blob: null, notFound: true };
    if (!res.ok) {
      console.error('Plaid institution logo fetch failed:', res.status, await res.text());
      return { blob: null, notFound: false };
    }

    return { blob: await res.blob(), notFound: false };
  } catch (err) {
    console.error('Failed to fetch Plaid institution logo from connection service', err);
    return { blob: null, notFound: false };
  }
}

export const connectionClient = {
  query: {
    preview: async (
      connectionType: string,
      connectionId: string,
      teamUuid: string,
      query: string,
      timeout: number = 30000
    ): Promise<QueryPreviewResponse | null> => {
      try {
        const loggedIn = await authClient.isAuthenticated();
        if (!loggedIn) return null;

        const typeLower = connectionType.toLowerCase().replace(/_/g, '-');
        const headers = new Headers(await jwtHeader());
        headers.set('X-Team-Id', teamUuid);

        const res = await fetch(`${API_URL}/${typeLower}/query`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ connection_id: connectionId, query, format: 'json' }),
          signal: AbortSignal.timeout(timeout),
        });

        if (!res.ok) {
          console.error('Query preview failed:', await res.text());
          return null;
        }

        return (await res.json()) as QueryPreviewResponse;
      } catch (err) {
        console.error('Failed to fetch query preview', err);
        return null;
      }
    },
  },
  schemas: {
    // ignore case of connection type
    get: async (
      connectionType:
        | 'postgres'
        | 'POSTGRES'
        | 'mysql'
        | 'MYSQL'
        | 'mssql'
        | 'MSSQL'
        | 'snowflake'
        | 'SNOWFLAKE'
        | 'cockroachdb'
        | 'COCKROACHDB'
        | 'BIGQUERY'
        | 'mariadb'
        | 'MARIADB'
        | 'supabase'
        | 'SUPABASE'
        | 'neon'
        | 'NEON'
        | 'mixpanel'
        | 'MIXPANEL'
        | 'google_analytics'
        | 'GOOGLE_ANALYTICS'
        | 'plaid'
        | 'PLAID'
        | 'quickbooks'
        | 'QUICKBOOKS',
      connectionId: string,
      teamUuid: string,
      forceCacheRefresh: boolean = false,
      timeout: number = 60000,
      // Optional caller-supplied abort signal (e.g. an AI session's cancel
      // signal). Combined with the per-request timeout so the fetch aborts
      // as soon as either fires. Passing this lets the AI cancel button
      // interrupt slow schema fetches (e.g. uncached Google Analytics)
      // instead of waiting for them to complete before unblocking.
      signal?: AbortSignal
    ): Promise<SqlSchemaResponse | null> => {
      // This might get called on a public file (where the user might not be
      // logged in but can still access the file). If they're not logged in,
      // we won't make the request (to prevent the redirect). It'll fail silently
      const loggedIn = await authClient.isAuthenticated();
      if (!loggedIn) {
        console.log("User is not logged in, so we won't make a request to the connection service.");
        return null;
      }

      const headers = new Headers(await jwtHeader());
      headers.set('X-Team-Id', teamUuid);

      const signals: AbortSignal[] = [AbortSignal.timeout(timeout)];
      if (signal) signals.push(signal);
      const combinedSignal = signals.length === 1 ? signals[0] : AbortSignal.any(signals);

      const url = `${API_URL}/${connectionType.toLowerCase().replace(/_/g, '-')}/schema/${connectionId}?force_cache_refresh=${forceCacheRefresh}`;
      const res = await fetch(url, {
        method: 'GET',
        headers,
        signal: combinedSignal,
      });
      if (!res.ok) {
        throw new Error('Failed to get the schema from the connection service');
      }
      const data = await res.json();
      return SqlSchema.parse(data);
    },
  },
  test: {
    run: async ({
      type,
      typeDetails,
      teamUuid,
    }: {
      type: ConnectionType;
      typeDetails: ConnectionTypeDetails;
      teamUuid: string;
    }) => {
      try {
        const typeLower = type.toLowerCase().replace(/_/g, '-');
        const headers = new Headers(await jwtHeader());
        headers.set('X-Team-Id', teamUuid);

        const res = await fetch(`${API_URL}/${typeLower}/test`, {
          method: 'POST',
          headers,
          body: JSON.stringify(typeDetails),
        });

        // A non-2xx means the service failed *before* it could report a test
        // result, so the body is an error string rather than a TestSchema
        // object. Parsing it anyway threw a ZodError ("Expected object,
        // received string" — INTERNAL-8A), which the catch below turned into
        // "Network error ... make sure you're connected to the internet" — a
        // misleading message that hid the real diagnostic. The reported case
        // was a 424 right after the user enabled the SSH tunnel, i.e. the
        // tunnel error was exactly what they needed to read. Surface it: the
        // caller renders `message` as the form's error.
        if (!res.ok) {
          const message = await readConnectionErrorMessage(res);
          // warn, not error: the service answered and the user is being told
          // what went wrong, so this needs no Sentry event (captureConsole
          // forwards `console.error` only).
          console.warn(`[connectionClient.test.run] ${res.status} ${res.statusText}: ${message}`);
          return { connected: false, message };
        }

        const data = await res.json();
        return TestSchema.parse(data);
      } catch (err) {
        console.error('Failed to connect to connection service', err);
        return {
          connected: false,
          message:
            'Network error: failed to make connection. Make sure you’re connected to the internet and try again.',
        };
      }
    },
  },
  staticIps: {
    list: async (): Promise<string[] | null> => {
      try {
        const res = await fetch(`${API_URL}/static-ips`, {
          method: 'GET',
          headers: new Headers(await jwtHeader()),
        });
        // Static IPs are a non-critical, display-only feature. A non-2xx
        // response is an expected, non-actionable condition — most commonly a
        // 401 from an idle tab whose JWT expired, but also the connection
        // service being briefly unavailable. Bail before parsing so we don't
        // feed a non-JSON error body into Zod (which throws and, via Sentry's
        // captureConsole, would surface as a noisy error).
        if (!res.ok) {
          console.warn(`[staticIps.list] request failed: ${res.status} ${res.statusText}`);
          return null;
        }
        const data = await res.json();
        const { static_ips } = StaticIpsSchema.parse(data) as StaticIpsResponse;
        return static_ips;
      } catch (err) {
        // Network failure (offline/DNS) on a non-critical feature — degrade to
        // null and log at warn to avoid Sentry noise.
        console.warn('Failed to get the static ips from the connection service', err);
        return null;
      }
    },
  },
  plaid: {
    /**
     * GET /plaid/logo/:institutionId — PNG body on success; null if 404 (no logo), other errors, or
     * unauthenticated. Pass `forceCacheRefresh: true` to append `?force_cache_refresh=true` (same
     * as schema routes) so the connection service bypasses its in-process cache (used by
     * `usePlaidInstitutionLogo` after an initial 404).
     */
    getInstitutionLogo: async (
      institutionId: string,
      teamUuid: string,
      opts?: { timeout?: number; forceCacheRefresh?: boolean }
    ): Promise<Blob | null> => {
      const { blob } = await fetchPlaidInstitutionLogo(institutionId, teamUuid, opts);
      return blob;
    },
  },
  financial: {
    request: async <E extends FinancialEndpoint>(
      teamUuid: string,
      endpoint: E,
      params: FinancialRequestMap[E]
    ): Promise<FinancialRequestResult> => {
      try {
        const schema = financialSchemas[endpoint];
        const validated = schema.parse(params);

        // The /financial endpoint accepts anonymous requests, so don't trigger
        // a login redirect for non-logged-in users — pass through whatever
        // token is already cached, otherwise omit the Authorization header.
        const jwt = await authClient.getTokenOrRedirect(true);
        const headers = new Headers({ 'content-type': 'application/json' });
        if (jwt) {
          headers.set('authorization', `Bearer ${jwt}`);
        }
        if (teamUuid) {
          headers.set('X-Team-Id', teamUuid);
        }

        const res = await fetch(`${API_URL}/financial`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ endpoint, params: validated }),
        });

        if (!res.ok) {
          const errorText = await res.text();
          if (res.status === 429) {
            const retryAfterSeconds = parseRetryAfter(res.headers.get('retry-after'));
            return {
              data: null,
              error: financialDataRateLimitMessage(retryAfterSeconds),
              rateLimited: true,
              retryAfterSeconds,
            };
          }
          return {
            data: null,
            error: `Financial data request failed with status ${res.status}: ${errorText}`,
            rateLimited: false,
          };
        }

        const data = await res.json();
        return { data, error: null, rateLimited: false };
      } catch (err) {
        if (err instanceof z.ZodError) {
          return {
            data: null,
            error: `Invalid request parameters: ${err.errors.map((e) => e.message).join(', ')}`,
            rateLimited: false,
          };
        }
        console.error('Failed to fetch financial data from connection service', err);
        return {
          data: null,
          error: err instanceof Error ? err.message : 'Unknown error fetching financial data',
          rateLimited: false,
        };
      }
    },
  },
};

export type FinancialRequestResult = {
  data: unknown;
  error: string | null;
  rateLimited: boolean;
  retryAfterSeconds?: number;
};
